import React, { memo, useMemo } from 'react';
import { clsx } from '../../utils/clsx';
import { sidePanelRef } from '../../utils/restore-scroll-after-zoom';
import { useTimelineHeight } from '../../utils/use-context';
import { Z_INDEX_SIDE_PANEL } from '../../z-indices';
import { TICKS_HEIGHT } from '../ticks/constants';
import {
  getTracksHeight,
  TimelineTrackAndLayout,
} from '../utils/drag/calculate-track-heights';
import { TrackHeader } from './timeline-track-header';
import { TIMELINE_DIVDER_HEIGHT } from '@/features/remotion-editor/editor/constants';
import { trackLabels } from '@waka/compat/track-layout';
import {
  getZoneBoundaryIndex,
  ZONE_SEPARATOR_HEIGHT,
} from '../../utils/zone-utils';

const computeZoneLabels = (
  tracksAndLayout: TimelineTrackAndLayout[],
): string[] => {
  return trackLabels(tracksAndLayout.map((entry) => entry.track));
};

export const SIDE_PANEL_WIDTH = 224;

interface SidePanelProps {
  tracks: TimelineTrackAndLayout[];
  inbetweenTrackDropTrackIndex: number | null;
}

export const SidePanel = memo(
  ({
    tracks: tracksAndLayout,
    inbetweenTrackDropTrackIndex,
  }: SidePanelProps) => {
    const zoneBoundaryIndex = useMemo(
      () => getZoneBoundaryIndex(tracksAndLayout.map((track) => track.track)),
      [tracksAndLayout],
    );

    const zoneLabels = useMemo(
      () => computeZoneLabels(tracksAndLayout),
      [tracksAndLayout],
    );

    const sidePanelStyle = useMemo(
      () => ({
        minWidth: SIDE_PANEL_WIDTH,
        zIndex: Z_INDEX_SIDE_PANEL,
      }),
      [],
    );

    const timelineHeight = useTimelineHeight();

    const trackHeadersStyle = useMemo(
      () => ({
        minHeight: timelineHeight + TICKS_HEIGHT,
        height: getTracksHeight({ tracks: tracksAndLayout }) + TICKS_HEIGHT,
        paddingTop: TICKS_HEIGHT,
      }),
      [tracksAndLayout, timelineHeight],
    );

    return (
      <div
        ref={sidePanelRef}
        className="sticky left-0 flex h-full flex-col border-r-2 border-r-sys-style-neutral-default"
        style={sidePanelStyle}
      >
        <div
          style={{
            minHeight: TIMELINE_DIVDER_HEIGHT,
          }}
        />
        <div
          id="track-headers"
          className="sticky left-0 flex w-full shrink-0 flex-col bg-sys-bg-surface"
          style={trackHeadersStyle}
        >
          {tracksAndLayout.map((trackAndLayout, idx) => (
            <React.Fragment key={trackAndLayout.track.id}>
              {idx === zoneBoundaryIndex &&
              zoneBoundaryIndex > 0 &&
              zoneBoundaryIndex < tracksAndLayout.length ? (
                <div
                  className="w-full border-t-4 border-sys-style-neutral-default"
                  style={{ height: ZONE_SEPARATOR_HEIGHT }}
                />
              ) : null}
              <div
                className={clsx(
                  'relative flex w-full select-none',
                  inbetweenTrackDropTrackIndex !== null &&
                    idx === inbetweenTrackDropTrackIndex - 1
                    ? 'border-editor-starter-accent'
                    : 'border-black/20',
                )}
              >
                <TrackHeader
                  trackAndLayout={trackAndLayout}
                  trackIndex={idx}
                  zoneLabel={zoneLabels[idx] ?? ''}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  },
);

SidePanel.displayName = 'SidePanel';
